import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doGetContracts, doCreateContract, doDeleteContract } from '../../../store/contract.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';

import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';


import Button from '../../Button';
import AddContractModal from '../../modals/AddContractModal';
import ConfirmModal from '../../modals/ConfirmModal';
import Loader from '../../Loader';
import { IStaff } from '../../../models/employees.model';
import { errorVariant, infoVariant, successVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';

interface IProps {
  staff: IStaff | undefined;
  memberCode: string;
}

const StaffContract: FC<IProps> = ({ staff, memberCode }) => {
  const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();
  const { contracts, isLoading } = useSelector((state: RootState) => state.contract);

  const [addContractVisible, setAddContractVisible] = useState(false);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deleteContractVisible, setDeleteContractVisible] = useState(false);
  const [contractCode, setContractCode] = useState('');

  useEffect(() => {
    getContracts();
  }, []);

  const getContracts = async () => {
    const res = await dispatch(doGetContracts(memberCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onSubmitContract = async () => {
    if (!staff) return;
    if (!startDate || !endDate) {
      return enqueueSnackbar(t('campus.staff.message4'), infoVariant);
    }
    if (!contractFile) {
      return enqueueSnackbar(t('campus.staff.message5'), infoVariant);
    }

    const form = new FormData();
    form.append('startDate', startDate.slice(0, 4) + startDate.slice(5, 7) + startDate.slice(8));
    form.append('endDate', endDate.slice(0, 4) + endDate.slice(5, 7) + endDate.slice(8));
    form.append('contract', contractFile);

    const res = await dispatch(doCreateContract(staff.memberCode, form));

    switch (res?.status) {
      case 200:
        setStartDate('');
        setEndDate('');
        setContractFile(null);        
        setAddContractVisible(false);
        dispatch(doGetContracts(staff.memberCode));
        enqueueSnackbar(t('common.message.create'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onDeleteContract = async () => {
    if (!staff || !contractCode) return;

    const res = await dispatch(doDeleteContract(contractCode));

    switch (res?.status) {
      case 200:
        setDeleteContractVisible(false);
        dispatch(doGetContracts(staff.memberCode));
        enqueueSnackbar(t('common.message.delete'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('campus.staff.column6'),
      key: 'date',
      render: (value, record, index) => (
        <>
          {moment(contracts[index].startDate).format('YYYY.MM.DD')} ~ {moment(contracts[index].endDate).format('YYYY.MM.DD')}
        </>
      ),
    },
    {
      title: t('campus.staff.column3'),
      key: 'file',
      render: (value, record, index) => (
        <div className='text-left'>
          <a className='btn-text' href={contracts[index].fileLocation} download={contracts[index].originalName}>
            {contracts[index].originalName}
          </a>
        </div>
      ),
    },
    {
      title: t('campus.staff.column4'),
      key: 'file',
      render: (value, record, index) => moment(contracts[index].regDate).format('YYYY.MM.DD'),
    },
    {
      title: t('campus.staff.column5'),
      key: 'name',
      render: (value, record, index) => (i18n.language === 'ko' ? contracts[index].regMemKorName : contracts[index].regMemEngName),
    },
    {
      title: '',
      key: 'button',
      render: (value, record, index) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className='btn-basic small gray'
            onClick={() => {
              setContractCode(contracts[index].contractCode);
              setDeleteContractVisible(true);
            }}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
  ];
  
  return (
    <div>
      {isLoading ? <Loader /> : null}
      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={contracts}
      />
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className='btn-basic pink-line'
            onClick={() => setAddContractVisible(true)}
            text={t('campus.staff.button3')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {addContractVisible &&
        <AddContractModal
          addContractVisible={addContractVisible}
          setAddContractVisible={setAddContractVisible}
          onSubmitContract={onSubmitContract}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          contractFile={contractFile}
          setContractFile={setContractFile}
        />
      }
      {deleteContractVisible &&
        <ConfirmModal
          visible={deleteContractVisible}
          setVisible={setDeleteContractVisible}
          onConfirm={onDeleteContract}
          titleText={t('campus.staff.title3')}
          messageText={t('campus.staff.text2')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      }
    </div>
  );
}

export default StaffContract;
